/**
 * A court tablet, the wall or a player's phone whose link has been replaced or taken away: the
 * whole screen says so, and nothing under it can be tapped. Without it the screen kept its last
 * picture under an Offline pill, which looked like the Wi-Fi. The admin screen has its own panel
 * (Admin.tsx), so it never shows this.
 */

import { useEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import type { Role } from "../../../shared/protocol";
import { shortPrefix } from "../../../shared/short";
import { linkStopped, subscribeLinkStopped } from "../lib/client";
import "./LinkStopped.css";

export function LinkStopped({ role }: { role: Role }) {
  const reason = useSyncExternalStore(subscribeLinkStopped, linkStopped, linkStopped);
  const show = reason !== null && role !== "admin";

  // what is under the cover cannot be reached by a tap, a key or a screen reader
  useEffect(() => {
    if (!show) return;
    const root = document.getElementById("root");
    root?.setAttribute("inert", "");
    return () => root?.removeAttribute("inert");
  }, [show]);

  if (!show) return null;
  const wall = role === "projector";
  const again = againAddress(role);
  return createPortal(
    <div className={"link-stopped" + (wall ? " link-stopped-wall" : "")} role="alert">
      <div className="link-stopped-card">
        <h1>This link has stopped</h1>
        {again ? (
          <>
            <p>Open {again.replace(/^https?:\/\//, "")} again.</p>
            <a className="btn" href={again}>Open it again</a>
          </>
        ) : (
          <p>{role === "player" ? "Scan the code on the big screen to get back in." : "Ask an admin for the new link."}</p>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * The short address that brings this screen back (decision 51): the wall, a court or the players'
 * page on the real day or the practice. Null for a test tournament, which has none.
 */
function againAddress(role: Role): string | null {
  const prefix = shortPrefix(new URLSearchParams(location.search).get("t") ?? "main");
  if (prefix === null) return null;
  if (role === "projector") return `${location.origin}${prefix}/wall`;
  if (role === "player") return `${location.origin}${prefix}`;
  if (role === "court") {
    const n = Number(location.pathname.split("/").filter(Boolean)[2]);
    return n >= 1 && n <= 4 ? `${location.origin}${prefix}/court${n}` : null;
  }
  return null;
}
